import clsx from 'clsx';
import { CornerDownLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { matchesSearch } from '@pz/core';
import i18n from '../i18n';
import { useDebounced, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';
import { useNav } from '../lib/nav';

/**
 * Search for sections (request 16 of round 4): finds the pages and the tabs within pages that the person may open and
 * opens one with Enter. The names of all three languages count, so "Überstunden", "overtime" and "fazla mesai" find the
 * same tab. Ctrl+K (Cmd+K) opens it from every page.
 * Round 5 (request 32): a project number or name lists the tabs of that project, a name of a person the staff
 * evaluation or the person page. The server decides what comes back ("search.find"), with the rules of the pages
 * themselves: nobody finds a project or a person through the search that the page would not show them. Contents
 * (bookings, texts) are never searched.
 */

interface Target {
  key: string;
  to: string;
  /** "Section › Tab" in the language of the person */
  label: string;
  group: string;
  /** names in every language, for the search */
  words: string;
}

const LANGS = ['de', 'en', 'tr'] as const;
/** a text in all three languages (for the search); unknown keys add nothing */
const inAll = (key: string) =>
  LANGS.map((l) => {
    const v = i18n.getFixedT(l)(key as never) as unknown;
    return typeof v === 'string' && v !== key ? v : '';
  }).join(' ');

/** Text keys of the pages of the menu (the menu holds the labels in the language of the person only). */
const ROUTE_KEYS: Record<string, string[]> = {
  '/': ['nav.overview'],
  '/week': ['nav.week'],
  '/entries': ['nav.entries'],
  '/my-absences': ['nav.myAbsences'],
  '/projects': ['nav.projects'],
  '/tasks': ['nav.tasks'],
  '/team': ['nav.team'],
  '/approvals': ['nav.approvalsGroup', 'nav.subTime'],
  '/leave-requests': ['nav.approvalsGroup', 'nav.leaveRequests'],
  '/planning': ['nav.planningGroup', 'nav.planning'],
  '/workload': ['nav.planningGroup', 'nav.workload'],
  '/cockpit': ['nav.insightsGroup', 'nav.cockpit'],
  '/analysis': ['nav.insightsGroup', 'nav.analysis'],
  '/evaluation': ['nav.insightsGroup', 'nav.evaluation'],
  '/customers': ['nav.customers'],
  '/billing': ['nav.billing'],
  '/people': ['nav.peopleGroup', 'nav.people'],
  '/absences': ['nav.peopleGroup', 'nav.absences'],
  '/announcements': ['nav.announcements'],
  '/admin': ['nav.admin'],
};

interface Inner {
  to: string;
  sectionKey: string;
  tabKey: string;
  show: boolean;
}

/** Tabs inside pages (the menu knows the pages); the rights are those of the pages. */
function useInnerTargets(): Inner[] {
  const { can } = useMe();
  const hr = can('hr.manage');
  return [
    // same order and rights as the tabs of Verwaltung (Admin.tsx, request 35 of round 5)
    { sectionKey: 'nav.admin', tabKey: 'admin.tabs.periods', to: '/admin?tab=periods', show: can('period.manage', 'time.manage', 'billing.manage') },
    { sectionKey: 'nav.admin', tabKey: 'admin.tabs.forma', to: '/admin?tab=forma', show: can('sync.manage', 'projects.manage') },
    { sectionKey: 'nav.admin', tabKey: 'admin.tabs.jobs', to: '/admin?tab=jobs', show: can('settings.manage', 'sync.manage') },
    { sectionKey: 'nav.admin', tabKey: 'admin.tabs.settings', to: '/admin?tab=settings', show: can('settings.manage') },
    { sectionKey: 'nav.admin', tabKey: 'admin.tabs.outlook', to: '/admin?tab=outlook', show: can('settings.manage', 'hr.manage') },
    { sectionKey: 'nav.admin', tabKey: 'admin.tabs.audit', to: '/admin?tab=audit', show: can('audit.view') },
    { sectionKey: 'nav.admin', tabKey: 'admin.tabs.backup', to: '/admin?tab=backup', show: can('backup.manage', 'data.export.all') },
    { sectionKey: 'nav.admin', tabKey: 'admin.tabs.categories', to: '/admin?tab=categories', show: can('settings.manage') },
    { sectionKey: 'nav.admin', tabKey: 'admin.tabs.templates', to: '/admin?tab=templates', show: can('projects.manage') },
    { sectionKey: 'nav.absences', tabKey: 'leave.overviewTab', to: '/absences?tab=overview', show: hr },
    { sectionKey: 'nav.absences', tabKey: 'leave.allTab', to: '/absences?tab=all', show: hr },
    { sectionKey: 'nav.absences', tabKey: 'overtime.tab', to: '/absences?tab=overtime', show: hr },
    { sectionKey: 'nav.absences', tabKey: 'leave.holidaysTab', to: '/absences?tab=holidays', show: can('hr.manage', 'settings.manage') },
    { sectionKey: 'nav.absences', tabKey: 'leave.eventsTab', to: '/absences?tab=events', show: can('hr.manage', 'settings.manage') },
    { sectionKey: 'nav.customers', tabKey: 'customers.tabs.contacts', to: '/customers?tab=contacts', show: can('customers.manage') },
    { sectionKey: 'nav.customers', tabKey: 'customers.tabs.companies', to: '/customers?tab=companies', show: can('customers.manage') },
    { sectionKey: 'nav.customers', tabKey: 'customers.tabs.terms', to: '/customers?tab=terms', show: can('customers.manage') },
    { sectionKey: 'profile.title', tabKey: 'profile.title', to: '/profile', show: true },
    { sectionKey: 'nav.help', tabKey: 'nav.help', to: '/help', show: true },
  ];
}

function useTargets(): Target[] {
  const { t } = useTranslation();
  const { groups } = useNav();
  const inner = useInnerTargets();
  const innerKey = inner.map((x) => `${x.to}:${x.show}`).join('|');
  return useMemo(() => {
    const out: Target[] = [];
    const seen = new Set<string>();
    for (const g of groups) {
      for (const s of g.sections) {
        for (const tab of s.tabs) {
          if (!tab.show || seen.has(tab.to)) continue;
          seen.add(tab.to);
          const several = s.tabs.filter((x) => x.show).length > 1;
          const label = several ? `${s.label} › ${tab.label}` : s.label;
          out.push({ key: tab.to, to: tab.to, label, group: g.label, words: `${label} ${(ROUTE_KEYS[tab.to] ?? []).map(inAll).join(' ')}` });
        }
      }
    }
    for (const x of inner) {
      if (!x.show || seen.has(x.to)) continue;
      seen.add(x.to);
      const section = String(t(x.sectionKey as never));
      const tab = String(t(x.tabKey as never));
      const label = section === tab ? section : `${section} › ${tab}`;
      out.push({ key: x.to, to: x.to, label, group: section, words: `${label} ${inAll(x.sectionKey)} ${inAll(x.tabKey)}` });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, innerKey, t]);
}

const PROJECT_TAB_KEYS = {
  overview: 'projects.tabs.overview',
  analysis: 'projects.tabs.analysis',
  kpi: 'projects.tabs.kpi',
  members: 'projects.tabs.members',
  tasks: 'projects.tabs.tasks',
  forma: 'projects.tabs.forma',
  fee: 'projects.tabs.fee',
} as const;

/** Projects and people for the query, as targets (request 32); empty below 2 characters. */
function useEntityTargets(query: string, open: boolean): { targets: Target[]; loading: boolean } {
  const { t } = useTranslation();
  const q = useDebounced(query.trim(), 200);
  const res = useQ('search.find', { q }, { enabled: open && q.length >= 2, placeholderData: undefined });
  const data = q.length >= 2 ? res.data : undefined;
  const targets = useMemo(() => {
    const out: Target[] = [];
    if (!data) return out;
    for (const p of data.projects) {
      const head = `${p.number} ${p.name}${p.archived ? ` (${t('search.archived')})` : ''}`;
      for (const tab of p.tabs) {
        out.push({
          key: `project:${p.id}:${tab}`,
          to: tab === 'overview' ? `/projects/${p.id}` : `/projects/${p.id}?tab=${tab}`,
          label: `${head} › ${t(PROJECT_TAB_KEYS[tab])}`,
          group: t('search.projects'),
          words: '',
        });
      }
    }
    for (const x of data.people) {
      const head = `${x.name}${x.active ? '' : ` (${t('search.inactive')})`}`;
      for (const target of x.targets) {
        out.push(
          target === 'evaluation'
            ? { key: `person:${x.id}:evaluation`, to: `/evaluation?person=${x.id}`, label: `${head} › ${t('nav.evaluation')}`, group: t('search.people'), words: '' }
            : { key: `person:${x.id}:record`, to: `/people/${x.id}`, label: `${head} › ${t('search.personRecord')}`, group: t('search.people'), words: '' },
        );
      }
    }
    return out;
  }, [data, t]);
  return { targets, loading: open && q.length >= 2 && res.isFetching && !data };
}

export function SectionSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const targets = useTargets();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const entities = useEntityTargets(query, open);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((x) => !x);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);
  // sections first (what the search is mainly for), then the projects and people of the query
  const results = useMemo(
    () => [...(query.trim() ? targets.filter((x) => matchesSearch(x.words, query)) : targets).slice(0, 40), ...(query.trim() ? entities.targets : [])],
    [targets, query, entities.targets],
  );
  const go = (x: Target | undefined) => {
    if (!x) return;
    setOpen(false);
    navigate(x.to);
  };
  const mac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-8 items-center gap-2 rounded-md border border-line px-2 text-sm text-graphite hover:border-line-strong hover:text-ink sm:w-56"
        aria-label={t('search.open')}
        title={t('search.open')}
      >
        <Search className="size-4 shrink-0" aria-hidden />
        <span className="hidden flex-1 truncate text-left sm:block">{t('search.placeholder')}</span>
        <kbd className="hidden rounded border border-line px-1 font-sans text-[11px] text-faint sm:block">{mac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>
      {open && (
        <div className="fixed inset-0 z-[55] flex items-start justify-center bg-ink/30 px-3 pt-[12vh] print:hidden" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div role="dialog" aria-modal="true" aria-label={t('search.title')} className="w-full max-w-xl overflow-hidden rounded-xl border border-line bg-sheet shadow-2xl">
            <div className="flex items-center gap-2 border-b border-line px-3">
              <Search className="size-4 shrink-0 text-faint" aria-hidden />
              <input
                ref={input}
                role="combobox"
                aria-expanded="true"
                aria-controls="section-search-list"
                aria-activedescendant={results[active] ? `ss-${active}` : undefined}
                className="h-12 min-w-0 flex-1 bg-transparent text-sm outline-none"
                placeholder={t('search.inputPlaceholder')}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setOpen(false);
                  else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setActive((i) => Math.min(results.length - 1, i + 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActive((i) => Math.max(0, i - 1));
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    go(results[active]);
                  }
                }}
              />
              <kbd className="rounded border border-line px-1 text-[11px] text-faint">Esc</kbd>
            </div>
            <ul id="section-search-list" role="listbox" className="max-h-[50vh] overflow-y-auto py-1">
              {results.length === 0 && !entities.loading && <li className="px-4 py-3 text-sm text-graphite">{t('search.none')}</li>}
              {entities.loading && <li className="px-4 py-2 text-xs text-graphite">{t('search.searching')}</li>}
              {results.map((x, i) => (
                <li key={x.key} id={`ss-${i}`} role="option" aria-selected={i === active}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(x)}
                    className={clsx('flex w-full items-center gap-3 px-4 py-2 text-left text-sm', i === active ? 'bg-accent-soft text-accent' : 'hover:bg-sheet-2')}
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">{x.label}</span>
                    <span className="shrink-0 text-xs text-graphite">{x.group}</span>
                    {i === active && <CornerDownLeft className="size-3.5 shrink-0" aria-hidden />}
                  </button>
                </li>
              ))}
            </ul>
            <p className="border-t border-line px-4 py-2 text-xs text-faint">{t('search.hint')}</p>
          </div>
        </div>
      )}
    </>
  );
}
